import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { Field } from '@reckon-web/field';
import {
  Form,
  FormValue,
  InitialValueInput,
  ValidatedFormValue,
  field,
  validate,
} from '@reckon-web/forms';
import { Stack } from '@reckon-web/stack';
import { AutocompleteField, BaseOption } from '@reckon-web/autocomplete';
import { makeId } from '@reckon-web/utils';

import { DatePickerField } from '../common/date-picker-field';
import { SelectField } from '../common/select-field';
import { useGetMembersList } from '../../data/useGetMembersList';
import { useGetTaskTypes } from '../../data/useGetTaskTypes';
import { useJobAutoComplete } from '../../hooks/use-job-auto-complete';
import {
  GetClientsForJobVariables,
  useGetClientsForJob,
} from '../../hooks/use-get-clients-for-job';
import { taskStatuses } from '../../globals';
import { getTaskStatusLabel } from '../common/task-status-badge';
import {
  GetEntitiesByIdsQueryVariables,
  useGetEntitiesByIds,
} from '../../data/useGetEntitiesByIds';
import { nameStatus } from '../../utils/name-status-utils';
import { ContentEditable } from '../common/content-editable';
import { TextField } from '../common/text-field';
import { useGetVisibleJobStatuses } from '../../hooks/use-get-visible-job-statuses';
import { JobStatusEnum } from '../job/types/job-status-enum';

type mode = 'create' | 'edit';

export const taskFormSchema = field.object({
  name: field.text({
    validate: validate((value) => {
      validate.required(value);
      return value;
    }),
  }),
  job: field.autocomplete()({
    validate: validate((value) => {
      validate.nonNullable(value);
      return value;
    }),
  }),
  client: field.select()({
    validate: validate((value) => {
      // validate.required(value);
      return value;
    }),
  }),
  taskType: field.select()({
    validate: validate((value) => {
      validate.required(value);
      return value;
    }),
  }),
  status: field.select()({
    validate: validate((value) => {
      validate.required(value);
      return value;
    }),
  }),
  assignedTo: field.select()({
    validate: validate((value) => {
      // validate.required(value);
      return value;
    }),
  }),
  dueDate: field.date({
    validate: validate((value) => {
      // validate.required(value);
      return value;
    }),
  }),
  // not actually text, should be number, but field.number doesn't exist
  // I guess I could use currency. But Joss said "nope, currency is specifically for dollar figures"
  timeEstimate: field.text({
    validate: validate((value) => {
      // blank string is a valid value
      if (typeof value === 'string' && value !== '') {
        const parsed = parseFloat(value);
        if (isNaN(parsed)) {
          validate.error('Must be a number');
        } else if (parsed < 0) {
          validate.positiveNumber(parsed);
        }
      }
      return value;
    }),
  }),
  description: field.string({
    validate: validate((value) => {
      // validate.required(value);
      return value;
    }),
  }),
});

export type TaskFormType = typeof taskFormSchema;
export type TaskFormForm = Form<TaskFormType>;
export type TaskFormInitialValueInput = InitialValueInput<TaskFormType>;
export type ValidatedTaskFormValue = ValidatedFormValue<TaskFormType>;

// this is some utitlity type madness
// basically, we want to end up with a type that has an optional array of strings for every value in the field
// Using Partial so that everything is optional
// The combination of Record and keyof gets us a type that has all the fields we need
// and FormValue is used to get the same type you get from "form = useForm(); form.value"
export type TaskFormExternalErrors = Partial<
  Record<keyof FormValue<TaskFormType>, string[]>
>;

// the taskStatuses array is already sorted
const statusOptions: BaseOption[] = taskStatuses.map((ts) => {
  return {
    label: getTaskStatusLabel(ts.id),
    value: ts.id,
  };
});

export const TaskForm: FC<{
  form: TaskFormForm;
  mode: mode;
  testId?: string;
  externalErrors?: TaskFormExternalErrors;
}> = ({ form, mode, externalErrors, testId = `task-form` }) => {
  const isCreate = mode === 'create';

  // tuck away the incoming errors - we'll hide them as they edit the fields, so we need a copy of them
  const [externalErrorsCopy, setExternalErrorsCopy] =
    useState<TaskFormExternalErrors>();
  useEffect(() => {
    setExternalErrorsCopy(externalErrors);
  }, [externalErrors]);

  const {
    loading: usersLoading,
    data: usersData,
    error: usersError,
  } = useGetMembersList();

  // the list of users, for use in the select field
  // simple memo, evalulate when the users load
  // Could be a good candidate for a custom hook?
  const userOptions: BaseOption[] = useMemo(() => {
    if (usersLoading || usersError || !usersData) {
      return [];
    }
    // make sure we also include the currently selected user, regardless of if they're able to be assigned a task
    // reason: need to be able to edit other details of the task
    // and retain the current assignee
    const assignableUsers = usersData.filter(
      (u) => (u.active && u.licensed) || u.cloudId === form.value.assignedTo
    );

    assignableUsers.sort((a, b) => {
      const aActive = a.licensed && a.active;
      const bActive = b.licensed && b.active;
      if (aActive && !bActive) {
        return -1;
      }
      if (!aActive && bActive) {
        return 1;
      }
      return (a.fullName || '').localeCompare(b.fullName || '', undefined, {
        sensitivity: 'base',
      });
    });

    const userOptions = assignableUsers.map((u) => {
      return { value: u.cloudId || '', label: nameStatus(u) };
    });

    userOptions.unshift({ label: 'None', value: '' });

    return userOptions;

    // deliberately doing the whole form, not the individual assignedTo.
    // We don't want this to reevaluate when the assignee changes, only when we're given a whole new form object
    // ie: we want the original user always available, not just whatever user they happened to select last
  }, [usersLoading, usersData, usersError, form]);

  // the available task types
  const {
    loading: taskTypesLoading,
    data: taskTypesData,
    error: taskTypesError,
  } = useGetTaskTypes();
  const taskTypeOptions = useMemo(() => {
    if (taskTypesLoading || taskTypesError || !taskTypesData) {
      return [];
    }

    const taskTypeOptions = taskTypesData.map((tt) => {
      return { value: tt.cloudId || '', label: tt.name || '', taskType: tt };
    });
    return taskTypeOptions;
  }, [taskTypesLoading, taskTypesData, taskTypesError]);

  const generalTaskType = taskTypesData?.find((tt) => tt.isAdhoc);

  const getEntityParams = useMemo(() => {
    if (!form.value.client) {
      return undefined;
    }
    return {
      cloudIds: [form.value.client],
    } as GetEntitiesByIdsQueryVariables;
  }, [form.value.client]);

  const {
    loading: entityLoading,
    data: entityData,
    error: entityError,
  } = useGetEntitiesByIds({
    variables: getEntityParams,
    skip: !getEntityParams,
  });

  const availableTaskTypeOptions = useMemo(() => {
    if (!taskTypeOptions) {
      return [];
    }

    // task type is a mandatory field, we must have something in the list. General is supposed to be valid for EVERYTHING
    const general = taskTypeOptions.find((x) => x.taskType.isAdhoc);
    const fallbackOptions = general ? [general] : [];

    // if there's no selection, everything is valid
    if (!getEntityParams?.cloudIds) {
      return taskTypeOptions;
    }

    // while we are loading, nothing is valid. But, keep general in there
    if (entityLoading || entityError || !entityData || !entityData.length) {
      return fallbackOptions;
    }

    // once we are loaded, restrict the list to whatever is applicable to this entity type
    const validTypes = taskTypeOptions.filter((tt) =>
      tt.taskType.entityTypes?.includes(entityData[0].entityTypeId!)
    );
    // fallback to general if we've managed to come up with an empty list after filtering. This should never happen
    return validTypes || fallbackOptions;
  }, [
    entityLoading,
    entityData,
    entityError,
    getEntityParams,
    taskTypeOptions,
  ]);

  const hasJob = !!form.value.job;

  const visibleJobStatuses = useGetVisibleJobStatuses();
  const IdsOfStatusesThatAreNotClosed = useMemo(() => {
    return visibleJobStatuses.data
      ?.filter((x) => x.status !== JobStatusEnum.valuesEnum.Closed)
      .map((x) => x.cloudId);
  }, [visibleJobStatuses]);

  // this needs re-doing any time the value of the selected job changes
  // and at first load, if we've been given a job
  // including when it gets cleared? Could potentially special case when it gets cleared
  // But, in the onchange of job we drop the client selection, so no special casing needed now
  const entitiesParam = useMemo(() => {
    if (!form.value.job?.value) {
      return undefined;
    }
    return {
      cloudId: form.value.job?.value || '',
    } as GetClientsForJobVariables;
  }, [form.value.job]);

  const {
    loading: entitiesLoading,
    data: entitiesData,
    error: entitiesError,
  } = useGetClientsForJob({ variables: entitiesParam, skip: !entitiesParam });

  const clientOptions: BaseOption[] = useMemo(() => {
    if (entitiesLoading || entitiesError || !entitiesData) {
      return [];
    }
    const results = [...entitiesData]
      .filter((x) => !x.isDeleted && !x.endDateUtc)
      .sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', undefined, {
          sensitivity: 'base',
        })
      )
      .map((e) => {
        return {
          label: e.name || '',
          value: e.cloudId || '',
        };
      });
    results.unshift({ label: 'None', value: '' });
    return results;
  }, [entitiesLoading, entitiesData, entitiesError]);

  // special custom hook, designed to be awaited, produces everything we need to get a list of jobs
  const searchJobs = useJobAutoComplete();

  const loadJobs = useCallback(
    async (inputValue: string): Promise<BaseOption[]> => {
      const jobs = await searchJobs(inputValue, IdsOfStatusesThatAreNotClosed);
      if (jobs.data) {
        return jobs.data.map((j) => {
          return {
            value: j.job.cloudId || '',
            label:
              (j.job.name ? j.job.name : '') +
              (j.client?.name ? ` (${j.client.name})` : ''),
          };
        });
      }
      return [];
    },
    [searchJobs, IdsOfStatusesThatAreNotClosed]
  );

  return (
    <Stack gap="xlarge" data={{ testid: testId }}>
      <TextField
        {...form.fields.name.props}
        label="Task name*"
        placeholder="Enter task name"
        invalidMessage={
          form.fields.name.props.invalidMessage ||
          externalErrorsCopy?.name?.join('. ')
        }
        onChange={(e) => {
          form.fields.name.props.onChange(e);
          setExternalErrorsCopy((v) => {
            return {
              ...v,
              name: [],
            };
          });
          // setNameErrors(undefined);
        }}
        /* TODO Workflow-400 once balance supports test id's, replace this ID */
        id={makeId(testId, 'task-name')}
      />
      {isCreate && (
        <AutocompleteField
          {...form.fields.job.props}
          label="Job*"
          placeholder="Search to select job"
          invalidMessage={
            form.fields.job.props.invalidMessage ||
            externalErrorsCopy?.job?.join('. ')
          }
          onChange={(option) => {
            form.fields.job.props.onChange(option);
            setExternalErrorsCopy((v) => {
              return {
                ...v,
                job: [],
              };
            });

            // drop the selected client
            form.fields.client.props.onChange(undefined);
            // reset the task type back to general
            // but only if we have loaded it. Otherwise, their selection will still technically be valid
            // we will have loaded it, this is a paranoia check
            if (generalTaskType) {
              form.fields.taskType.props.onChange(
                generalTaskType.cloudId || ''
              );
            }
          }}
          loadOptions={loadJobs}
          data-testid={makeId(testId, 'job')}
        />
      )}
      {isCreate && (
        <SelectField
          {...form.fields.client.props}
          disabled={!hasJob || entitiesLoading}
          label="Client"
          placeholder="Select client..."
          invalidMessage={
            form.fields.client.props.invalidMessage ||
            externalErrorsCopy?.client?.join('. ')
          }
          description={
            !hasJob
              ? 'After you have selected a Job, you will be able to select a Client from the Job.'
              : undefined
          }
          options={clientOptions}
          onChange={(value) => {
            form.fields.client.props.onChange(value || undefined);
            setExternalErrorsCopy((v) => {
              return {
                ...v,
                client: [],
              };
            });
            // drop the selected task type. At this point they have to think
            // but drop it in a way that we don't instantly throw a validation error, that's a bit hinkey
            form.fields.taskType.setState({ value: undefined, touched: false });
          }}
          data-testid={makeId(testId, 'client')}
        />
      )}
      {isCreate && (
        <SelectField
          {...form.fields.taskType.props}
          disabled={taskTypesLoading || entityLoading}
          label="Task type*"
          placeholder="Select task type..."
          options={availableTaskTypeOptions}
          invalidMessage={
            form.fields.taskType.props.invalidMessage ||
            externalErrorsCopy?.taskType?.join('. ')
          }
          onChange={(value) => {
            form.fields.taskType.props.onChange(value);
            setExternalErrorsCopy((v) => {
              return {
                ...v,
                taskType: [],
              };
            });
          }}
          data-testid={makeId(testId, 'task-type')}
        />
      )}
      <SelectField
        {...form.fields.status.props}
        label="Status*"
        placeholder="Select status..."
        options={statusOptions}
        invalidMessage={
          form.fields.status.props.invalidMessage ||
          externalErrorsCopy?.status?.join('. ')
        }
        onChange={(value) => {
          form.fields.status.props.onChange(value);
          setExternalErrorsCopy((v) => {
            return {
              ...v,
              status: [],
            };
          });
        }}
        data-testid={makeId(testId, 'status')}
      />
      <SelectField
        {...form.fields.assignedTo.props}
        label="Assigned to"
        placeholder="Select assignee..."
        options={userOptions}
        disabled={usersLoading}
        invalidMessage={
          form.fields.assignedTo.props.invalidMessage ||
          externalErrorsCopy?.assignedTo?.join('. ')
        }
        onChange={(value) => {
          form.fields.assignedTo.props.onChange(value || undefined);
          setExternalErrorsCopy((v) => {
            return {
              ...v,
              assignedTo: [],
            };
          });
        }}
        data-testid={makeId(testId, 'assigned-to')}
      />
      <DatePickerField
        {...form.fields.dueDate.props}
        label="Due date"
        invalidMessage={
          form.fields.dueDate.props.invalidMessage ||
          externalErrorsCopy?.dueDate?.join('. ')
        }
        onChange={(value) => {
          form.fields.dueDate.props.onChange(value);
          setExternalErrorsCopy((v) => {
            return {
              ...v,
              dueDate: [],
            };
          });
        }}
        data-testid={makeId(testId, 'due-date')}
      />
      <TextField
        {...form.fields.timeEstimate.props}
        label="Time estimate (hours)"
        placeholder="0"
        type="number"
        invalidMessage={
          form.fields.timeEstimate.props.invalidMessage ||
          externalErrorsCopy?.timeEstimate?.join('. ')
        }
        onChange={(e) => {
          form.fields.timeEstimate.props.onChange(e);
          // form.fields.description.setState({touched: true, value: ''});
          setExternalErrorsCopy((v) => {
            return {
              ...v,
              timeEstimate: [],
            };
          });
        }}
        onBlur={() => {
          form.fields.timeEstimate.props.onBlur();
          // make sure they don't type in anything less good, like 4.44. Round it to the nearest 0.05 so that we
          // get a value that multiplies out to an even number of minutes, and divides back down to an hours value that
          // has max 2 decimal places
          const rawHours = form.value.timeEstimate;
          if (!rawHours) {
            // empty string, bail, nothing more to do
            return;
          }

          const parsedHours = parseFloat(rawHours);
          const roundedHours = Math.ceil(parsedHours * 20 - 0.5) / 20;
          if (parsedHours !== roundedHours) {
            // value must have been changed from the raw. Poke it back into the form
            form.fields.timeEstimate.setState((prev) => ({
              ...prev,
              value: roundedHours.toFixed(2),
            }));
          }
        }}
        data-testid={makeId(testId, 'time-estimate')}
      />
      <Field
        {...form.fields.description.props}
        label="Description"
        invalidMessage={
          form.fields.description.props.invalidMessage ||
          externalErrorsCopy?.description?.join('. ')
        }
        data-testid={makeId(testId, 'description')}
      >
        {({ inputProps, labelId }) => (
          <ContentEditable
            {...inputProps}
            {...form.fields.description.props}
            labelId={labelId}
            placeholder="Enter description"
            onChange={(value) => {
              form.fields.description.props.onChange(value);
              setExternalErrorsCopy((v) => {
                return {
                  ...v,
                  description: [],
                };
              });
            }}
          />
        )}
      </Field>
    </Stack>
  );
};
